// Vista V°B° del ingeniero — rediseñada con KPIs, mapa con polyline y panel lateral.
// Layout:
//   - Header compacto
//   - 4 KPIs coloreados (duración, distancia, vel media, cobertura)
//   - Mapa grande (polígono lote + polyline recorrido + marcadores inicio/fin) | Panel lateral (inicio, fin, vel max, tiempos muertos, clima)
//   - Strip horizontal de confirmación
//   - CTA grande "Dar V°B° y Archivar"

import React, { useMemo } from 'react';
import GeoMapPreview from '../../components/GeoMapPreview';
import {
  calcularMetricas, calcularCobertura,
  severidadDuracion, severidadVelocidadMedia, severidadCobertura, severidadTiemposMuertos,
  type SeveridadKPI,
} from './trackingMetricas';
import { ConexionBanner } from '../../components/ConexionBanner';
import type { AppBoleta, AppBoletaCampoTrack } from '../../types/empacadora';

interface ReporteCampoMin {
  id: number;
  registrado_por?: string | null;
  updated_at?: string | null;
  fin_at?: string | null;
  inicio_at?: string | null;
  inicio_lat?: number | null;
  inicio_lng?: number | null;
  fin_lat?: number | null;
  fin_lng?: number | null;
  // clima
  clima_temp_c?: number | null;
  clima_humedad_pct?: number | null;
  clima_viento_kmh?: number | null;
  clima_viento_dir_deg?: number | null;
  clima_precipitacion_mm?: number | null;
  clima_codigo?: number | null;
}

interface Props {
  boletaSel: AppBoleta;
  reporteCampo: ReporteCampoMin | null;
  loteGeojson: any | null;
  tracks: AppBoletaCampoTrack[];
  anchoBoom: number | null;
  sinReporte: boolean;
  sinFinalizar: boolean;
  bloqueado: boolean;
  guardando: boolean;
  darVoBo: () => Promise<void> | void;
  onVolver: () => void;
  renderMsgBanner: () => React.ReactNode;
}

// ── Helpers de formato ──────────────────────────────────────────────────────

function fmtFecha(s?: string | null) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtHora(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit', hour12: false });
}
function fmtDuracion(seg: number): string {
  if (!Number.isFinite(seg) || seg <= 0) return '—';
  const min = Math.round(seg / 60);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}
function fmtDistancia(m: number): string {
  if (!Number.isFinite(m) || m <= 0) return '—';
  if (m < 1000) return `${m.toFixed(0)} m`;
  return `${(m / 1000).toFixed(2)} km`;
}
function fmtNum(n: number | null | undefined, dec = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// Colores por severidad
function colorKPI(sev: SeveridadKPI): { bg: string; border: string; text: string; numero: string } {
  switch (sev) {
    case 'ok':   return { bg: 'rgba(34,197,94,0.08)',  border: 'rgba(34,197,94,0.30)',  text: '#86efac', numero: '#4ade80' };
    case 'warn': return { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.35)', text: '#fcd34d', numero: '#fbbf24' };
    case 'bad':  return { bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.35)',  text: '#fca5a5', numero: '#f87171' };
  }
}

// WMO weather code → icono + label corto
function descClima(codigo: number | null | undefined): { icono: string; label: string } {
  if (codigo == null) return { icono: '—', label: 'Sin dato' };
  if (codigo === 0) return { icono: '☀', label: 'Despejado' };
  if (codigo <= 3) return { icono: '⛅', label: 'Parcial' };
  if (codigo <= 48) return { icono: '🌫', label: 'Niebla' };
  if (codigo <= 57) return { icono: '🌦', label: 'Llovizna' };
  if (codigo <= 67) return { icono: '🌧', label: 'Lluvia' };
  if (codigo <= 77) return { icono: '🌨', label: 'Nieve' };
  if (codigo <= 82) return { icono: '🌧', label: 'Chubasco' };
  if (codigo <= 99) return { icono: '⛈', label: 'Tormenta' };
  return { icono: '☁', label: 'Nublado' };
}

// ── Componente KPI ──────────────────────────────────────────────────────────

function KPI({ label, valor, severidad, sub }: { label: string; valor: string; severidad: SeveridadKPI; sub?: string }) {
  const c = colorKPI(severidad);
  return (
    <div style={{
      padding: '14px 16px', borderRadius: '14px',
      background: c.bg, border: `1px solid ${c.border}`,
      display: 'flex', flexDirection: 'column', gap: '4px',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: c.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: '24px', fontWeight: 800, color: c.numero, lineHeight: 1 }}>
        {valor}
      </div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--ink-faint)' }}>{sub}</div>}
    </div>
  );
}

// ── Render principal ────────────────────────────────────────────────────────

export function VistaVoBo(p: Props): React.ReactElement {
  const { boletaSel, reporteCampo, loteGeojson, tracks, anchoBoom,
          sinReporte, sinFinalizar, bloqueado, guardando, darVoBo, onVolver, renderMsgBanner } = p;

  // Cálculo de métricas
  const metricas = useMemo(() => calcularMetricas(tracks), [tracks]);
  const cobertura = useMemo(() => {
    if (!loteGeojson || !anchoBoom || tracks.length < 2) return null;
    return calcularCobertura(tracks, loteGeojson, anchoBoom);
  }, [tracks, loteGeojson, anchoBoom]);

  // Severidades para KPIs (sin bloquear nada, solo informativo)
  const sevDuracion   = severidadDuracion(metricas.duracion_s / 60);
  const sevVelMedia   = severidadVelocidadMedia(metricas.velocidad_media_kmh);
  const sevCobertura  = cobertura ? severidadCobertura(cobertura.cobertura_pct) : 'warn' as SeveridadKPI;
  const sevTMuertos   = severidadTiemposMuertos(metricas.tiempos_muertos.length);

  // GeoJSON combinado para el mapa: polígono del lote + polyline recorrido + puntos inicio/fin
  const mapaGeojson = useMemo(() => {
    const features: any[] = [];
    if (loteGeojson) {
      // Normalizar a Feature
      if (loteGeojson.type === 'FeatureCollection') {
        features.push(...(loteGeojson.features ?? []));
      } else if (loteGeojson.type === 'Feature') {
        features.push(loteGeojson);
      } else {
        features.push({ type: 'Feature', properties: { rol: 'lote' }, geometry: loteGeojson });
      }
    }
    // Polyline del recorrido
    if (tracks.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { rol: 'recorrido' },
        geometry: { type: 'LineString', coordinates: tracks.map(t => [Number(t.lng), Number(t.lat)]) },
      });
    }
    // Puntos inicio/fin
    const iLat = reporteCampo?.inicio_lat != null ? Number(reporteCampo.inicio_lat) : null;
    const iLng = reporteCampo?.inicio_lng != null ? Number(reporteCampo.inicio_lng) : null;
    const fLat = reporteCampo?.fin_lat != null ? Number(reporteCampo.fin_lat) : null;
    const fLng = reporteCampo?.fin_lng != null ? Number(reporteCampo.fin_lng) : null;
    if (iLat != null && iLng != null) features.push({ type: 'Feature', properties: { rol: 'inicio' }, geometry: { type: 'Point', coordinates: [iLng, iLat] } });
    if (fLat != null && fLng != null) features.push({ type: 'Feature', properties: { rol: 'fin' }, geometry: { type: 'Point', coordinates: [fLng, fLat] } });
    if (features.length === 0) return null;
    return { type: 'FeatureCollection', features };
  }, [loteGeojson, tracks, reporteCampo]);

  const iLat = reporteCampo?.inicio_lat != null ? Number(reporteCampo.inicio_lat) : null;
  const iLng = reporteCampo?.inicio_lng != null ? Number(reporteCampo.inicio_lng) : null;
  const climaDesc = descClima(reporteCampo?.clima_codigo);

  return (
    <div style={{ minHeight: '100%', background: 'var(--surface-base)' }}>
      <ConexionBanner />
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

        {/* Header compacto */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <button onClick={onVolver} style={{
            padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
            border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
          }}>← Volver</button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#f8fafc' }}>V°B° del Ingeniero</div>
            <div style={{ fontSize: '13px', color: 'var(--ink-muted)' }}>
              Lote {boletaSel.lote_num} · {boletaSel.etapa_nombre} · Sem. {boletaSel.semana} · Aplicó {boletaSel.aplicado_por ?? '—'} · {fmtFecha(boletaSel.aplicado_at?.slice(0,10))}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
        }}>
          <KPI label="Duración"
               valor={fmtDuracion(metricas.duracion_s)}
               severidad={sevDuracion}
               sub={metricas.puntos > 0 ? `${metricas.puntos} muestras GPS` : 'Sin tracking GPS'} />
          <KPI label="Distancia"
               valor={fmtDistancia(metricas.distancia_m)}
               severidad="ok"
               sub={metricas.puntos < 2 ? 'Sin recorrido' : 'Recorrido total'} />
          <KPI label="Vel. media"
               valor={metricas.velocidad_media_kmh > 0 ? `${fmtNum(metricas.velocidad_media_kmh, 1)} km/h` : '—'}
               severidad={sevVelMedia}
               sub={metricas.velocidad_max_kmh > 0 ? `Máx ${fmtNum(metricas.velocidad_max_kmh, 1)} km/h` : undefined} />
          <KPI label="Cobertura"
               valor={cobertura ? `${fmtNum(cobertura.cobertura_pct, 0)}%` : '—'}
               severidad={cobertura ? sevCobertura : 'warn'}
               sub={cobertura
                 ? `${fmtNum(cobertura.area_cubierta_m2 / 10000, 2)} / ${fmtNum(cobertura.area_lote_m2 / 10000, 2)} ha`
                 : (!anchoBoom ? 'Falta ancho del boom' : 'Sin recorrido o lote')} />
        </div>

        {/* Strip de diagnóstico (temporal) — explica por qué faltan métricas */}
        <div style={{
          padding: '8px 14px', borderRadius: '8px',
          background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.2)',
          fontSize: '12px', color: 'var(--ink-muted)',
          display: 'flex', flexWrap: 'wrap', gap: '14px',
        }}>
          <span><strong style={{ color: tracks.length === 0 ? '#fca5a5' : '#4ade80' }}>Tracks GPS:</strong> {tracks.length}</span>
          <span><strong style={{ color: anchoBoom == null ? '#fca5a5' : '#4ade80' }}>Ancho boom:</strong> {anchoBoom != null ? `${anchoBoom} m` : 'no definido'}</span>
          <span><strong style={{ color: loteGeojson == null ? '#fca5a5' : '#4ade80' }}>Perímetro lote:</strong> {loteGeojson != null ? 'sí' : 'no'}</span>
          <span><strong>Boom ID:</strong> {String(boletaSel.boom_id ?? '—')}</span>
          <span><strong>Lote ID:</strong> {boletaSel.lote_id ? boletaSel.lote_id.slice(0, 8) + '…' : '—'}</span>
        </div>

        {renderMsgBanner()}

        {/* Mapa + Panel lateral */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '14px',
          alignItems: 'stretch',
          minHeight: '380px',
        }}>
          {/* Mapa */}
          <div style={{
            padding: '14px', borderRadius: '16px',
            background: 'var(--surface-raised)', border: '1px solid var(--line)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ fontSize: '12px', color: 'var(--ink-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
              Recorrido sobre el lote {boletaSel.lote_num}
            </div>
            <div style={{ flex: 1, minHeight: '340px' }}>
              {mapaGeojson ? (
                <GeoMapPreview
                  geojson={mapaGeojson}
                  gps={iLat != null && iLng != null ? { lat: iLat, lng: iLng } : null}
                  height="100%"
                  maxWidthClassName="max-w-none"
                  polygonColor={loteGeojson ? '#22c55e' : '#38bdf8'}
                />
              ) : (
                <div style={{
                  height: '100%', minHeight: '340px', borderRadius: '10px',
                  border: '1px dashed var(--line)', background: 'var(--surface-overlay)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '20px', textAlign: 'center', gap: '8px',
                }}>
                  <span style={{ fontSize: '32px', opacity: 0.5 }}>🗺️</span>
                  <div style={{ fontSize: '14px', color: 'var(--ink-muted)', fontWeight: 600 }}>Sin ubicación disponible</div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-faint)', maxWidth: '300px' }}>
                    Sin perímetro del lote ni recorrido GPS registrado.
                  </div>
                </div>
              )}
            </div>
            {!loteGeojson && tracks.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#fbbf24' }}>
                ⚠ El lote no tiene perímetro registrado. Se muestra solo el recorrido GPS.
              </div>
            )}
            {!anchoBoom && (
              <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--ink-faint)' }}>
                ⓘ Cargá el ancho del boom en Plan de Manejo para calcular cobertura.
              </div>
            )}
          </div>

          {/* Panel lateral */}
          <div style={{
            padding: '14px', borderRadius: '16px',
            background: 'var(--surface-raised)', border: '1px solid var(--line)',
            display: 'flex', flexDirection: 'column', gap: '12px',
          }}>
            {/* Inicio / Fin */}
            <div>
              <div style={{ fontSize: '11px', color: 'var(--ink-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Inicio · Fin</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />
                  <span style={{ color: 'var(--ink-muted)', minWidth: 38 }}>Inicio</span>
                  <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{fmtHora(reporteCampo?.inicio_at)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
                  <span style={{ color: 'var(--ink-muted)', minWidth: 38 }}>Fin</span>
                  <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{fmtHora(reporteCampo?.fin_at)}</span>
                </div>
              </div>
            </div>

            {/* Tiempos muertos */}
            <div>
              <div style={{ fontSize: '11px', color: 'var(--ink-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Tiempos muertos</div>
              {metricas.tiempos_muertos.length === 0 ? (
                <div style={{ fontSize: '13px', color: '#4ade80' }}>Sin paradas mayores a 2 min</div>
              ) : (
                <div style={{ fontSize: '13px', color: colorKPI(sevTMuertos).numero, fontWeight: 600 }}>
                  {metricas.tiempos_muertos.length} parada{metricas.tiempos_muertos.length === 1 ? '' : 's'} · {fmtDuracion(metricas.tiempo_muerto_total_s)}
                </div>
              )}
            </div>

            {/* Clima al inicio */}
            <div>
              <div style={{ fontSize: '11px', color: 'var(--ink-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Clima al inicio</div>
              {reporteCampo?.clima_temp_c != null ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>{climaDesc.icono}</span>
                    <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{fmtNum(reporteCampo.clima_temp_c, 0)}°C</span>
                    <span style={{ color: 'var(--ink-faint)' }}>· {climaDesc.label}</span>
                  </div>
                  <div style={{ color: 'var(--ink-muted)' }}>HR {fmtNum(reporteCampo.clima_humedad_pct, 0)}% · Viento {fmtNum(reporteCampo.clima_viento_kmh, 0)} km/h</div>
                  {reporteCampo.clima_precipitacion_mm != null && reporteCampo.clima_precipitacion_mm > 0 && (
                    <div style={{ color: '#93c5fd' }}>Lluvia {fmtNum(reporteCampo.clima_precipitacion_mm, 1)} mm/h</div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--ink-faint)' }}>Sin registro meteorológico</div>
              )}
            </div>
          </div>
        </div>

        {/* Strip de confirmación */}
        {!bloqueado && (
          <div style={{
            padding: '12px 16px', borderRadius: '12px',
            background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px',
            fontSize: '13px', color: 'var(--ink-muted)',
          }}>
            <span style={{ color: '#4ade80', fontWeight: 700 }}>Al dar V°B° usted confirma:</span>
            <span>✓ Revisó productos y cantidades aplicadas</span>
            <span>✓ Las justificaciones de ajuste son aceptadas</span>
            <span>✓ La boleta queda archivada y lista para imprimir</span>
          </div>
        )}

        {/* Banner bloqueo si aplica */}
        {sinReporte ? (
          <div style={{
            padding: '14px 16px', borderRadius: '12px',
            background: 'rgba(245,158,11,0.10)', border: '1.5px solid rgba(245,158,11,0.4)',
            display: 'flex', alignItems: 'flex-start', gap: '12px',
          }}>
            <span style={{ fontSize: '22px' }}>⚠</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#fbbf24', marginBottom: '2px' }}>
                Falta el reporte de campo del boomero
              </div>
              <div style={{ fontSize: '13px', color: 'var(--ink-muted)' }}>
                No se puede dar V°B° hasta que el boomero registre el reporte en Terminal Boomero.
              </div>
            </div>
          </div>
        ) : sinFinalizar ? (
          <div style={{
            padding: '14px 16px', borderRadius: '12px',
            background: 'rgba(245,158,11,0.10)', border: '1.5px solid rgba(245,158,11,0.4)',
            display: 'flex', alignItems: 'flex-start', gap: '12px',
          }}>
            <span style={{ fontSize: '22px' }}>⏳</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#fbbf24', marginBottom: '2px' }}>
                Aplicación en curso — boomero no ha finalizado
              </div>
              <div style={{ fontSize: '13px', color: 'var(--ink-muted)' }}>
                El boomero debe presionar "Finalizar aplicación" para que se pueda dar V°B°.
              </div>
            </div>
          </div>
        ) : null}

        {/* CTA */}
        <button onClick={darVoBo} disabled={guardando || bloqueado} style={{
          width: '100%', padding: '20px', borderRadius: '14px', fontSize: '18px', fontWeight: 800,
          border: 'none',
          background: guardando || bloqueado ? 'rgba(255,255,255,0.07)' : 'linear-gradient(135deg,#16a34a,#22c55e)',
          color: guardando || bloqueado ? 'rgba(255,255,255,0.3)' : '#fff',
          cursor: guardando || bloqueado ? 'not-allowed' : 'pointer', letterSpacing: '0.02em',
        }}>
          {guardando
            ? 'Registrando...'
            : sinReporte
              ? 'Esperando reporte de campo del boomero'
              : sinFinalizar
                ? 'Esperando que boomero finalice'
                : '✓ Dar V°B° y Archivar'}
        </button>
      </div>
    </div>
  );
}
